/**
 * The menu: one continuous list of the whole catalogue, and a console at the
 * foot of the panel to move around it with.
 *
 * **It was a category at a time and now it is all of it.** The rail down the
 * left showed one category's products and cost 160px of a 1080px panel to do
 * it; with 31 categories in a branch it was also a scrolling column of 31
 * near-identical cups with three-line labels at 16px, because the glyph
 * fallback has eight patterns and most category names match the coffee one.
 * A customer looking for a lemonade read a wall of coffee cups.
 *
 * Now every enabled product is in one scroller. A strip of categories under
 * the wordmark slides to the section in view; each group still carries its
 * own title in the list, the way a menu on a wall is read. A tap on the
 * strip jumps there. There is no second set of chips at the foot.
 *
 * **The console is at the foot because the panel is bolted to the floor.** Its
 * top edge is above most people's eyeline and — the part that is not a matter
 * of taste — an operable control on a kiosk has to sit within 380-1220mm of
 * the floor to be reachable from a wheelchair, which on this panel is the
 * lower half of the glass and no higher. The basket and the way forward stay
 * there. The categories sit under the wordmark with search and language:
 * they are asked about the *screen*, and a second copy of them at the foot
 * was the same name written twice.
 *
 * The two exceptions are **search and language**, which sit in the header. They
 * are asked once, they are asked about the *screen* rather than about the
 * order, and between them they were spending 168px of the only strip that
 * moves a customer through the menu. The header comment says what that costs.
 *
 * **The basket is a bar and not a column.** It was a 24rem rail down the right
 * edge, which showed the customer every line they had added; the bar shows a
 * count and a total instead and puts the lines one tap away on the checkout
 * screen. That is a real trade — a customer who cannot see what they added
 * keeps adding — and it is made because the panel is portrait: a rail costs a
 * third of the width the products are in. The count badge is what is left of
 * the rail's job, and it nudges when it changes so the addition is seen rather
 * than merely recorded.
 */
import { memo, useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  basePrice,
  categorySort,
  defaultTemperature,
  displayName,
  format,
  imageFor,
  isDiscounted,
  isOrderable,
  needsConfiguration,
  searchMenu,
  tileTemperature
} from '@mgc/domain'
import type { Category, Locale, Product, SearchOutcome } from '@mgc/domain'
import { Icon } from '@/components/icons'
import {
  ActionBar,
  Button,
  Notice,
  Plaque,
  ProductImage,
  ScreenHeader,
  Sheet,
  TemperatureTag,
  Total
} from '@/components/ui'
import { Keyboard } from '@/components/keyboard'
import { LocaleSwitch } from '@/components/locale-switch'
import { RemovedNotice } from '@/components/removed-notice'
import { Wordmark } from '@/components/brand'
import { BASKET_CATCH, cancelFlight, catchBasket, flyIntoBasket } from '@/components/fly-into-basket'
import { useT } from '@/i18n'
import { useKiosk, usePricedCart } from '@/store/kiosk'
import { useSession } from '@/store/session'
import { sectionPlaceholderRem, sectionTop, spyCategory } from './menu-nav'

const HOUR_MS = 60 * 60 * 1000

/** A screenful of search hits. More than this and the customer is scrolling, not choosing. */
const MAX_RESULTS = 60

/**
 * Below this many, a tile says how many are left.
 *
 * Five, because the point is "decide now", not inventory. At twenty a customer
 * reads it as a stock report and ignores it; at one it arrives too late to
 * change what they pick. `balance` has always been on the wire and always
 * enforced by `addLine`, and was never shown — so the only way a customer
 * learned a drink was nearly gone was by being refused at the last one.
 */
const LOW_STOCK = 5

interface Section {
  readonly category: Category
  readonly products: readonly Product[]
}

export function Menu() {
  const t = useT()
  const menu = useKiosk((s) => s.menu)
  const orderType = useKiosk((s) => s.cart.orderType)
  const openProduct = useKiosk((s) => s.openProductModal)
  const setStep = useKiosk((s) => s.setStep)
  const reset = useKiosk((s) => s.reset)
  const servedAt = useKiosk((s) => s.menuServedAt)
  const removed = useKiosk((s) => s.removed)
  const branch = useSession((s) => s.branch)
  const { lines, total } = usePricedCart()

  const [query, setQuery] = useState('')
  /**
   * Whether the keyboard is up — which is a different fact from whether a
   * search is running, and conflating the two was the whole problem. Closing
   * the keyboard used to clear the query, so a customer had two states and no
   * third: keys covering the bottom of the panel, or no search at all. There
   * was no way to put the keyboard down and look at what you had found.
   */
  const [typing, setTyping] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const scroller = useRef<HTMLDivElement>(null)
  const sectionEls = useRef(new Map<string, HTMLElement>())
  const [active, setActive] = useState<string | null>(null)
  /**
   * A chip tap scrolls smoothly, and a smooth scroll passes through every
   * heading on the way. Without this the strip flickers through eleven
   * categories on its way to the twelfth, which reads as a fault.
   */
  const settling = useRef<string | null>(null)
  const settleGen = useRef(0)
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const locale = useKiosk((s) => s.locale)
  const searching = query.trim().length > 0
  // ~100 products in a branch, and `searchMenu` walks all of them ranking four
  // fields on every keystroke — well under a millisecond, but this component
  // owns the keyboard driving it, so the typing stays ahead of the results.
  const deferredQuery = useDeferredValue(query)

  /**
   * The whole catalogue, grouped, once.
   *
   * Empty categories are dropped rather than rendered as a heading over
   * nothing — 1C sends categories a branch does not stock, and a heading with
   * no products under it is a customer scrolling for something that is not
   * there.
   */
  const sections = useMemo<readonly Section[]>(() => {
    const products = menu?.products ?? []
    return [...(menu?.categories ?? [])]
      .sort(categorySort)
      .map((category) => ({
        category,
        products: products.filter((p) => p.enabled && p.categoryId === category.id)
      }))
      .filter((s) => s.products.length > 0)
  }, [menu])

  const results = useMemo(
    () =>
      searching
        ? searchMenu(menu?.products ?? [], deferredQuery, { limit: MAX_RESULTS, locale })
        : { hits: [], total: 0 },
    [menu, deferredQuery, searching, locale]
  )

  /**
   * The words the customer typed, for the one recovery that is worth offering.
   *
   * Every word has to match, so the usual way a good query becomes no results
   * is one word too many — and dropping the last one is cheaper to offer than
   * to explain.
   */
  const words = useMemo(
    () =>
      deferredQuery
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 0),
    [deferredQuery]
  )

  /** Something to tap when nothing matched. The top of the menu, which is where a browse would start anyway. */
  const suggestions = useMemo(() => (sections[0]?.products ?? []).slice(0, 6), [sections])

  useEffect(() => {
    if (active === null && sections.length > 0) setActive(sections[0]!.category.id)
  }, [sections, active])

  /**
   * Which section the customer is in.
   *
   * A scroll handler and not an `IntersectionObserver`: there is one target
   * per rail section, the answer is "the last section whose top is above the
   * viewport", and an observer would have to be told the same thing with a
   * rootMargin nobody can read. Tops come from `getBoundingClientRect` —
   * `offsetTop` is a guessed height while `content-visibility` is skipping
   * the section.
   */
  const onScroll = useCallback(() => {
    const box = scroller.current
    if (box === null || settling.current !== null) return
    const tops = []
    for (const { category } of sections) {
      const node = sectionEls.current.get(category.id)
      if (node !== undefined) tops.push({ id: category.id, top: sectionTop(box, node) })
    }
    const atEnd = box.scrollTop + box.clientHeight >= box.scrollHeight - 8
    const current = spyCategory(tops, box.scrollTop + 24, atEnd)
    if (current !== null) setActive(current)
  }, [sections])

  const jumpTo = useCallback(
    (categoryId: string) => {
      const box = scroller.current
      const el = sectionEls.current.get(categoryId)
      if (box === null || el === undefined) return

      /*
       * Skipped sections have a placeholder height. Measuring the target
       * against those guesses undershoots, then the real tiles appear and
       * the list jumps. Lay out everything up to the target first.
       */
      const revealed: HTMLElement[] = []
      for (const { category } of sections) {
        const node = sectionEls.current.get(category.id)
        if (node === undefined) continue
        node.style.contentVisibility = 'visible'
        revealed.push(node)
        if (category.id === categoryId) break
      }
      void box.offsetHeight

      setActive(categoryId)
      const dest = sectionTop(box, el)
      if (Math.abs(box.scrollTop - dest) < 2) {
        for (const node of revealed) node.style.contentVisibility = ''
        return
      }

      settling.current = categoryId
      settleGen.current += 1
      const gen = settleGen.current
      if (settleTimer.current !== null) clearTimeout(settleTimer.current)

      box.scrollTo({ top: dest, behavior: 'smooth' })

      let done = false
      const finish = () => {
        if (done || settleGen.current !== gen) return
        done = true
        box.removeEventListener('scrollend', finish)
        if (settleTimer.current !== null) {
          clearTimeout(settleTimer.current)
          settleTimer.current = null
        }
        const dest = sectionTop(box, el)
        if (Math.abs(box.scrollTop - dest) > 2) box.scrollTo({ top: dest })
        for (const node of revealed) node.style.contentVisibility = ''
        settling.current = null
      }
      box.addEventListener('scrollend', finish)
      settleTimer.current = setTimeout(finish, 1500)
    },
    [sections]
  )

  useEffect(
    () => () => {
      if (settleTimer.current !== null) clearTimeout(settleTimer.current)
    },
    []
  )

  const registerSection = useCallback((id: string, el: HTMLElement | null) => {
    if (el === null) sectionEls.current.delete(id)
    else sectionEls.current.set(id, el)
  }, [])

  // The thresholds are the branch's, not this file's. They were a hardcoded
  // six hours here, and the "stops selling" half `docs/offline.md` promises did
  // not exist at all.
  const ageMs = servedAt === null ? 0 : Date.now() - new Date(servedAt).getTime()
  const policy = branch?.menuStale
  const stale = servedAt !== null && policy !== undefined && ageMs > policy.warnHours * HOUR_MS
  const tooStaleToSell = servedAt !== null && policy?.refuseHours != null && ageMs > policy.refuseHours * HOUR_MS

  const count = lines.reduce((n, line) => n + line.selection.quantity, 0)
  const [added, setAdded] = useState(0)
  useEffect(() => {
    const onCatch = () => setAdded((n) => n + 1)
    window.addEventListener(BASKET_CATCH, onCatch)
    return () => window.removeEventListener(BASKET_CATCH, onCatch)
  }, [])

  // A warning strip that still lets a customer buy is a warning nobody acts
  // on. Past the refuse threshold the tiles stop opening.
  const open = useCallback(
    (product: Product) => {
      if (!tooStaleToSell) openProduct(product)
    },
    [tooStaleToSell, openProduct]
  )

  /** The keyboard goes away and the results stay. */
  const dismissKeyboard = () => setTyping(false)

  /** The search ends and the catalogue comes back. */
  const clearSearch = () => {
    setQuery('')
    setTyping(false)
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[var(--color-paper-raised)]">
      {/*
        The brand bar: the mark, what the customer chose one screen ago, and
        the screen's two utilities.

        **Search and language live up here, and that is a deliberate exception
        to the reach rule.** Everything that advances or completes an order —
        a category, a product, the basket, the way forward, the way back — is
        still in the band a hand reaches, and it always will be. These two are
        not that. They are asked once, they are asked *about the screen* rather
        than about the order, and down in the console they were costing 168px
        of the only strip that moves a customer through 31 categories.

        The cost is real and worth writing down: at the top of a floor-standing
        panel they sit above the 1220mm the ADA puts operable parts under, so a
        customer in a wheelchair may not reach either one. Neither is on the
        path to buying a coffee, which is what makes the trade survivable — but
        if a third utility ever wants to join them, that is the moment to stop
        and put the row back at the bottom.
      */}
      <ScreenHeader tone="brand" size="slim">
        <div className="flex min-w-0 flex-1 items-center justify-between gap-6">
          <Wordmark on="brand" className="w-80" />

          <div className="flex shrink-0 items-center gap-4">
            {orderType !== null && (
              <span className="display-upright inline-flex items-center gap-2.5 rounded-[var(--radius-pill)] bg-[var(--color-brand-ink)] px-6 py-2.5 text-[length:var(--text-kiosk-sm)] text-[var(--color-brand)] uppercase">
                <Icon name={orderType === 'eat_in' ? 'cup' : 'bag'} className="size-6" />
                {orderType === 'eat_in' ? t('orderType.eatIn') : t('orderType.takeOut')}
              </span>
            )}

            <button type="button" onClick={() => setTyping(true)} aria-label={t('menu.search')} className="tap">
              <Plaque icon="search" tone="paper" size="small" />
            </button>

            <button type="button" onClick={() => setLanguageOpen(true)} aria-label={t('menu.language')} className="tap">
              <Plaque icon="language" tone="paper" size="small" />
            </button>
          </div>
        </div>
      </ScreenHeader>

      {(stale || removed.length > 0) && (
        <div className="flex shrink-0 flex-col gap-3 px-10 pt-6">
          {/*
            Two thresholds, and only the first of them existed. Past the second
            the shop stops selling rather than trading on prices nobody can
            vouch for — `docs/offline.md` has always said this is an operator's
            policy, and now it is one.
          */}
          {tooStaleToSell ? (
            <Notice tone="bad">{t('menu.tooStale')}</Notice>
          ) : (
            stale && <Notice tone="warn">{t('menu.stale')}</Notice>
          )}
          <RemovedNotice />
        </div>
      )}

      {searching ? (
        <SearchResults
          query={deferredQuery}
          outcome={results}
          words={words}
          suggestions={suggestions}
          orderType={orderType}
          locale={locale}
          onOpen={open}
          onClear={clearSearch}
          onDropLastWord={() => setQuery(words.slice(0, -1).join(' '))}
        />
      ) : (
        <>
          <CategoryStrip sections={sections} active={active} locale={locale} onPick={jumpTo} />
          <div
            ref={scroller}
            onScroll={onScroll}
            className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain"
          >
            <Catalogue
              sections={sections}
              orderType={orderType}
              locale={locale}
              onOpen={open}
              register={registerSection}
            />
          </div>
        </>
      )}

      {/*
        The keys and the collapsed query share this slot, and the basket bar
        is always the last child. The keyboard used to dock *under* the bar,
        so opening search lifted the total by ~44rem — a layout shift of the
        one object that is supposed to stay put through the order. In this
        slot the bar never moves; the catalogue shrinks from the bottom and
        the tiles the customer is looking at stay where they were.
      */}
      {typing ? (
        <SearchSheet
          value={query}
          onChange={setQuery}
          onDismiss={dismissKeyboard}
          onClear={clearSearch}
          found={results.hits.length}
          total={results.total}
        />
      ) : (
        searching && (
          <SearchBar
            query={query}
            found={results.hits.length}
            total={results.total}
            onOpen={() => setTyping(true)}
            onClear={clearSearch}
          />
        )
      )}

      {/*
        Four things and only one of them is a word.

        "Шинээр эхлэх" was a text button here and it cost about 230px of a bar
        that also has to carry a 52px total and the one call to action — so
        `42,500₮` was being truncated to `42…` on the screen whose whole job is
        to say what the basket costs. It is a home plaque now, which is the
        glyph it already carried when it lived in the header.
      */}
      <ActionBar>
        <button type="button" onClick={reset} aria-label={t('menu.startOver')} className="tap shrink-0">
          <Plaque icon="home" tone="onDark" size="small" />
        </button>

        <button
          type="button"
          data-basket
          key={added}
          onClick={() => setStep('checkout')}
          aria-label={t('cart.title')}
          className={`tap relative shrink-0 ${added > 0 ? 'animate-added' : ''}`}
        >
          <span className="grid size-20 place-items-center rounded-[var(--radius-chip)] bg-white/10 text-[var(--color-brand)]">
            <Icon name="cart" className="size-10" />
          </span>
          {count > 0 && (
            <span className="animate-badge-in display-upright absolute -top-3 -right-3 grid size-12 place-items-center rounded-full bg-[var(--color-brand)] text-[length:var(--text-kiosk-sm)] text-[var(--color-brand-ink)] ring-4 ring-[var(--color-dark)]">
              {count}
            </span>
          )}
        </button>

        <Total className="flex-1" label={t('cart.total')} value={format(total)} />

        <Button
          size="large"
          onDark
          className="shrink-0 uppercase"
          disabled={lines.length === 0}
          onClick={() => setStep('checkout')}
        >
          {t('cart.checkout')}
          <Icon name="forward" className="size-9" />
        </Button>
      </ActionBar>

      {/*
        It closes on the choice, not on a separate "done": there are two
        options and picking one is the whole interaction.
      */}
      <Sheet open={languageOpen} onClose={() => setLanguageOpen(false)} labelledBy="language-title">
        <div className="flex flex-col items-center gap-10 p-12">
          <h2 id="language-title" className="display text-[length:var(--text-kiosk-lg)] uppercase">
            {t('menu.language')}
          </h2>
          <LocaleSwitch ground="paper" onChoose={() => setLanguageOpen(false)} />
        </div>
      </Sheet>
    </div>
  )
}

/**
 * Four tiles across the 1080. At 18rem it was three, which put two and a half
 * rows of a hundred-drink menu on the glass at once. It stays `auto-fill`
 * rather than a hardcoded `repeat(4,…)` because the same bundle has to lay
 * out in a browser window during `pnpm dev` — and 14rem is what lets four fit
 * inside 67.5rem less the `px-10` gutters, and not five.
 */
const TILE_GRID = 'grid auto-rows-min grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-5'

/**
 * Every product in the branch, in one column.
 *
 * Memoised on the sections and nothing else. The strip re-renders on every
 * scroll frame as the spy moves, and without this each of those frames would
 * walk every tile in the branch to discover none had changed.
 */
const Catalogue = memo(function Catalogue({
  sections,
  orderType,
  locale,
  onOpen,
  register
}: {
  sections: readonly Section[]
  orderType: 'eat_in' | 'take_out' | null
  locale: Locale
  onOpen: (product: Product) => void
  register: (id: string, el: HTMLElement | null) => void
}) {
  const t = useT()
  if (sections.length === 0) {
    return <p className="py-32 text-center text-[var(--color-ink-soft)]">{t('menu.empty')}</p>
  }
  return (
    <>
      {sections.map(({ category, products }) => (
        <section
          key={category.id}
          ref={(el) => register(category.id, el)}
          className="menu-section"
          style={{ containIntrinsicBlockSize: `auto ${String(sectionPlaceholderRem(products.length))}rem` }}
          aria-labelledby={`section-${category.id}`}
        >
          <h2
            id={`section-${category.id}`}
            className="flex items-end justify-between gap-6 border-b border-[var(--color-line)] bg-[var(--color-paper-raised)] px-10 pt-8 pb-5"
          >
            <span className="display min-w-0 flex-1 truncate text-[length:var(--text-kiosk-xl)]">
              {displayName(category, locale)}
            </span>
            <span className="display-upright shrink-0 text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-faint)]">
              {products.length} {t('menu.items')}
            </span>
          </h2>
          <div className={`${TILE_GRID} px-10 py-8`}>
            {products.map((product) => (
              <ProductTile key={product.id} product={product} orderType={orderType} locale={locale} onOpen={onOpen} />
            ))}
          </div>
        </section>
      ))}
    </>
  )
})

/**
 * What a search found, in place of the catalogue.
 *
 * Search spans the whole catalogue rather than the section the customer
 * happens to be scrolled to: somebody who types "латте" and is shown only the
 * latte in one category has been given a worse answer than the one they asked
 * for.
 *
 * **Nothing found is a screen with a way out of it.** It was one line of grey
 * text, which tells a customer their query failed and leaves them holding it.
 * Every word has to match, so the ordinary way a good query becomes nothing is
 * one word too many — and offering to drop the last one costs less than
 * explaining why "хүйтэн латте зөөлөн" found no drinks.
 */
function SearchResults({
  query,
  outcome,
  words,
  suggestions,
  orderType,
  locale,
  onOpen,
  onClear,
  onDropLastWord
}: {
  query: string
  outcome: SearchOutcome
  words: readonly string[]
  suggestions: readonly Product[]
  orderType: 'eat_in' | 'take_out' | null
  locale: Locale
  onOpen: (product: Product) => void
  onClear: () => void
  onDropLastWord: () => void
}) {
  const t = useT()
  const { hits, total } = outcome
  return (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      <h2 className="flex items-end justify-between gap-6 border-b border-[var(--color-line)] bg-[var(--color-paper-raised)] px-10 pt-8 pb-5">
        <span className="display min-w-0 flex-1 truncate text-[length:var(--text-kiosk-xl)]">
          {t('menu.searchResults')}
        </span>
        {/*
          "60 items" was the cap presented as a fact about the menu: the screen
          printed the length of the capped list and called it the number of
          matches. It now says which of the two number it is showing.
        */}
        <span className="display-upright shrink-0 text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-faint)] tabular-nums">
          {hits.length < total ? `${hits.length} ${t('menu.searchOf')} ${total}` : `${total} ${t('menu.items')}`}
        </span>
      </h2>
      {hits.length === 0 ? (
        <div className="flex flex-col items-center gap-9 px-10 py-20">
          <p className="text-center text-[length:var(--text-kiosk-lg)] text-[var(--color-ink-soft)]">
            {t('menu.searchNothing')} “{query}”
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            {words.length > 1 && (
              <Button variant="primary" onClick={onDropLastWord}>
                {t('menu.searchDropWord')}
              </Button>
            )}
            <Button variant="ghost" onClick={onClear}>
              {t('menu.searchBrowseAll')}
            </Button>
          </div>

          {suggestions.length > 0 && (
            <div className="flex w-full flex-col gap-6 pt-6">
              <span className="eyebrow text-center text-[var(--color-ink-faint)]">{t('menu.searchTryThese')}</span>
              <div className={TILE_GRID}>
                {suggestions.map((product) => (
                  <ProductTile
                    key={product.id}
                    product={product}
                    orderType={orderType}
                    locale={locale}
                    onOpen={onOpen}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={`${TILE_GRID} px-10 py-8`}>
          {hits.map((product) => (
            <ProductTile key={product.id} product={product} orderType={orderType} locale={locale} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * One row of categories, and it slides.
 *
 * Not a heading and not a second strip at the foot. The pill rides under the
 * active name and the row itself scrolls so that name stays in the middle —
 * flicking the catalogue moves the strip, tapping a chip jumps the catalogue.
 */
function CategoryStrip({
  sections,
  active,
  locale,
  onPick
}: {
  sections: readonly Section[]
  active: string | null
  locale: Locale
  onPick: (categoryId: string) => void
}) {
  const strip = useRef<HTMLDivElement>(null)
  const chips = useRef(new Map<string, HTMLButtonElement>())
  const primed = useRef(false)
  const cameraTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [overflowing, setOverflowing] = useState(false)
  const [thumb, setThumb] = useState({ x: 0, w: 0, ready: false })

  const measure = useCallback(() => {
    const box = strip.current
    if (box === null) return
    setOverflowing(box.scrollWidth > box.clientWidth + 1)
    const el = active === null ? undefined : chips.current.get(active)
    if (el === undefined) return
    setThumb({ x: el.offsetLeft, w: el.offsetWidth, ready: true })
  }, [active])

  useLayoutEffect(() => {
    measure()
    const box = strip.current
    if (box === null) return
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    return () => observer.disconnect()
  }, [measure, sections, locale])

  useEffect(() => {
    const box = strip.current
    const el = active === null ? undefined : chips.current.get(active)
    if (box === null || el === undefined) return
    const left = Math.max(0, el.offsetLeft - (box.clientWidth - el.offsetWidth) / 2)
    if (Math.abs(box.scrollLeft - left) < 12) return
    if (cameraTimer.current !== null) clearTimeout(cameraTimer.current)
    const run = () => {
      cameraTimer.current = null
      box.scrollTo({ left, behavior: primed.current ? 'smooth' : 'auto' })
      primed.current = true
    }
    if (!primed.current) {
      run()
      return
    }
    cameraTimer.current = setTimeout(run, 120)
    return () => {
      if (cameraTimer.current !== null) clearTimeout(cameraTimer.current)
    }
  }, [active])

  return (
    <div className="shrink-0 border-b border-[var(--color-line)] bg-[var(--color-paper-sunk)]">
      <div
        ref={strip}
        style={
          overflowing
            ? {
                maskImage: 'linear-gradient(to right, transparent, #000 1.5rem, #000 calc(100% - 1.5rem), transparent)',
                WebkitMaskImage:
                  'linear-gradient(to right, transparent, #000 1.5rem, #000 calc(100% - 1.5rem), transparent)'
              }
            : undefined
        }
        className="no-scrollbar relative overflow-x-auto px-6 py-3"
      >
        <div className="relative flex w-max items-center gap-1">
          {thumb.ready && (
            <div
              aria-hidden
              className="pointer-events-none absolute top-0 bottom-0 rounded-[var(--radius-pill)] bg-[var(--color-dark)] shadow-[var(--shadow-lift)]"
              style={{
                width: thumb.w,
                transform: `translateX(${String(thumb.x)}px)`,
                transition: primed.current ? 'transform 280ms ease-out, width 280ms ease-out' : undefined
              }}
            />
          )}
          {sections.map(({ category }) => {
            const on = category.id === active
            return (
              <button
                key={category.id}
                type="button"
                ref={(el) => {
                  if (el === null) chips.current.delete(category.id)
                  else chips.current.set(category.id, el)
                }}
                onClick={() => onPick(category.id)}
                aria-current={on ? 'true' : undefined}
                className={`tap display-upright relative z-10 shrink-0 rounded-[var(--radius-pill)] px-7 text-[length:var(--text-kiosk)] whitespace-nowrap uppercase ${
                  on ? 'text-[var(--color-brand)]' : 'text-[var(--color-ink-soft)]'
                }`}
              >
                {displayName(category, locale)}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * The search field and its keyboard, as the console.
 *
 * A field at the top of the screen with a keyboard under it is the browser
 * arrangement and it does not survive being bolted to the floor: the field
 * ends up above the reach band and the keyboard covers its own results. Here
 * the keys are where the hand already is, the field sits directly over them,
 * and the results fill everything above.
 *
 * It is a **flex child, not `fixed`**. Fixed put ~44rem of keyboard on top of
 * a results list that reserved no space for it, so the last rows of tiles
 * could never be scrolled to; in the column, the layout subtracts its height
 * for us. It sits in the chips' slot, above the `ActionBar`, so the running
 * total stays on the glass and does not jump when the keys come up.
 *
 * A real `<input>`, so a physical keyboard works — `pnpm dev` in a browser is
 * a working kiosk and this is one of the places that could quietly stop being
 * true. While it holds focus the barcode wedge stands down by design
 * (`shell/wedge.ts`): a scanner firing into a focused field would enter the
 * barcode twice.
 */
function SearchSheet({
  value,
  onChange,
  onDismiss,
  onClear,
  found,
  total
}: {
  value: string
  onChange: (value: string) => void
  /** Puts the keys away and **keeps the query**. The results stay on screen. */
  onDismiss: () => void
  /** Ends the search. The catalogue comes back. */
  onClear: () => void
  found: number
  total: number
}) {
  const t = useT()
  const field = useRef<HTMLInputElement>(null)

  useEffect(() => {
    field.current?.focus()
  }, [])

  return (
    <div className="z-30 flex shrink-0 flex-col rounded-t-[var(--radius-sheet)] bg-[var(--color-paper-raised)] shadow-[var(--shadow-tile)]">
      <div className="animate-slide-up flex flex-col gap-5 p-8 pb-4">
        <div className="relative">
          <span className="absolute top-1/2 left-8 -translate-y-1/2 text-[var(--color-ink-faint)]">
            <Icon name="search" className="size-9" />
          </span>
          <input
            ref={field}
            type="search"
            inputMode="search"
            autoComplete="off"
            value={value}
            placeholder={t('menu.search')}
            aria-label={t('menu.search')}
            onChange={(event) => onChange(event.target.value)}
            className="w-full rounded-[var(--radius-pill)] border-2 border-[var(--color-brand)] bg-[var(--color-paper-sunk)] py-6 pr-44 pl-22 text-[length:var(--text-kiosk-lg)] font-semibold outline-none placeholder:text-[var(--color-ink-faint)]"
          />
          {/*
            The count changes on every keystroke and was visual only, so the
            customer who most needs to know whether "латте" found anything —
            the one not reading the grid behind the keyboard — was told
            nothing. Polite, so it waits for a pause in typing rather than
            interrupting every character.
          */}
          <span
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="display-upright absolute top-1/2 right-8 -translate-y-1/2 text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-faint)] tabular-nums"
          >
            {value.trim().length > 0 &&
              (found < total ? `${found} ${t('menu.searchOf')} ${total}` : `${total} ${t('menu.items')}`)}
          </span>
        </div>

        {/*
          `onClear` empties the field and leaves the keyboard up — the customer
          is still typing. `onClose` puts the keyboard away and keeps what they
          typed. Those were one action, and the one action threw the search
          away.
        */}
        <Keyboard
          onKey={(character) => onChange(value + character)}
          onBackspace={() => onChange(value.slice(0, -1))}
          onClear={value.trim().length > 0 ? onClear : () => onChange('')}
          onClose={onDismiss}
        />
      </div>
    </div>
  )
}

/**
 * The search with its keyboard put away.
 *
 * The third state the screen never had. It stands in the console's slot and
 * reads as the collapsed sheet: the query, how much of it is on screen, and
 * the one key that ends the search. Tapping the query itself brings the
 * keyboard back with the text intact.
 *
 * It used to carry a language key too, back when the category strip did. Both
 * moved to the header, which is the point of them being there — the customer
 * who cannot read this screen finds that control in the same place whether
 * they are browsing, typing, or reading results.
 */
function SearchBar({
  query,
  found,
  total,
  onOpen,
  onClear
}: {
  query: string
  found: number
  total: number
  onOpen: () => void
  onClear: () => void
}) {
  const t = useT()
  return (
    <div className="z-20 flex shrink-0 items-center gap-4 border-t border-[var(--color-line)] bg-[var(--color-paper-sunk)] px-6 py-5">
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('menu.search')}
        className="tap flex min-w-0 flex-1 items-center gap-5 self-stretch rounded-[var(--radius-pill)] border-2 border-[var(--color-brand)] bg-[var(--color-paper-raised)] px-8 py-5 text-left"
      >
        <Icon name="search" className="size-9 shrink-0 text-[var(--color-ink-faint)]" />
        <span className="display-upright min-w-0 flex-1 truncate text-[length:var(--text-kiosk)]">{query}</span>
        <span className="display-upright shrink-0 text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-faint)] tabular-nums">
          {found < total ? `${found} ${t('menu.searchOf')} ${total}` : `${total} ${t('menu.items')}`}
        </span>
      </button>

      <button
        type="button"
        onClick={onClear}
        aria-label={t('menu.searchClear')}
        className="tap grid w-20 shrink-0 place-items-center self-stretch rounded-[var(--radius-card)] bg-[var(--color-dark)] text-[var(--color-brand)]"
      >
        <Icon name="close" className="size-9" />
      </button>
    </div>
  )
}

const ProductTile = memo(function ProductTile({
  product,
  orderType,
  locale,
  onOpen
}: {
  product: Product
  orderType: 'eat_in' | 'take_out' | null
  locale: Locale
  onOpen: (product: Product) => void
}) {
  const t = useT()
  const photo = useRef<HTMLDivElement>(null)
  const available = isOrderable(product, orderType)
  const discounted = isDiscounted(product)
  const choosing = needsConfiguration(product)
  const fixedTemperature = tileTemperature(product) ?? null
  const faded = available ? '' : 'opacity-45'

  const addDirect = () => {
    const temperature = defaultTemperature(product)
    const flew = flyIntoBasket(photo.current, imageFor(product, temperature))
    const result = useKiosk.getState().add({
      productId: product.id,
      quantity: 1,
      ...(temperature === undefined ? {} : { temperature }),
      addCodes: []
    })
    if (!result.ok) {
      cancelFlight()
      return
    }
    if (!flew) catchBasket()
  }

  return (
    <div
      /*
        No shadow at rest. The border and the tinted ground already separate a
        tile from the page, and the scroller now holds the whole catalogue —
        hundreds of 45px blurs recomposited on every scroll frame, on a panel
        that has no GPU to spare.
      */
      className="group relative flex flex-col overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-line)] bg-[var(--color-paper-sunk)] text-left"
    >
      {/*
        The fade is on the parts, not the tile. It was `opacity-45` on this
        node, which faded the sold-out badge with everything else — white on
        #231F20 came out at 2.85:1, so the one label that says *why* a tile is
        grey was the faintest text on the screen.
      */}
      <button
        type="button"
        onClick={() => onOpen(product)}
        disabled={!available}
        className={`tap flex min-h-0 flex-1 flex-col text-left ${faded}`}
      >
        <div ref={photo} className="relative aspect-[3/4] w-full overflow-hidden bg-[var(--color-line)]">
          {/*
            No name in the stand-in: the tile prints it two inches below, and a
            grid of fallbacks was showing every product's name twice.
          */}
          {/*
            The tile states a serving fact, so it shows that fact's cup: an
            ice-only drink with an iced photograph draws it here, and a drink
            that still has to be asked draws the hot one — the sheet is where
            the picture changes, because that is where the choice is made.
          */}
          <ProductImage
            src={imageFor(product, defaultTemperature(product))}
            name={displayName(product, locale)}
            className="size-full"
          />
          {fixedTemperature !== null && (
            <TemperatureTag
              temperature={fixedTemperature}
              label={fixedTemperature === 'ice' ? t('product.ice') : t('product.hot')}
              iceLabel={t('product.ice')}
              className="absolute bottom-5 left-5 shadow-[var(--shadow-lift)]"
            />
          )}
        </div>
        {/*
          Sentence case at 700, not capitals at 900. Capitals take away the
          word's shape, which is what a customer scanning for «латте»
          recognises, and run ~15% wider in the ~195px a line has at four
          across; 900 at this size closes the counters of Ш, Ж, Ө and Ү.
        */}
        <span className="line-clamp-2 px-5 pt-4 font-bold text-[length:var(--text-kiosk)] leading-tight">
          {displayName(product, locale)}
        </span>
        {/*
          The price has the band to itself, so it keeps `.display` at `lg` —
          a step above the name, which is the order a customer reads them in.
          Four across, sharing the band with the add key left 24px for it.
        */}
        <span className="mt-auto flex min-w-0 flex-col border-t border-[var(--color-line)] px-5 pt-4 pb-5">
          <span className="display truncate text-[length:var(--text-kiosk-lg)] tabular-nums">
            {format(basePrice(product))}
          </span>
          {discounted && (
            <span className="display-upright text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-soft)] line-through">
              {format(product.price)}
            </span>
          )}
        </span>
      </button>

      {/*
        The add key sits on the photograph's free corner — the temperature
        tag holds the other. It cannot be inside the photograph, which is
        inside the open button, so an overlay the photograph's shape carries
        it. A ring rather than a shadow: a blur per tile is what the note
        above took away.
      */}
      <div className={`pointer-events-none absolute inset-x-0 top-0 aspect-[3/4] ${faded}`}>
        <button
          type="button"
          disabled={!available}
          aria-label={choosing ? displayName(product, locale) : t('product.add')}
          onClick={() => (choosing ? onOpen(product) : addDirect())}
          className="tap pointer-events-auto absolute right-4 bottom-4 grid size-16 place-items-center rounded-[var(--radius-chip)] bg-[var(--color-brand)] text-[var(--color-brand-ink)] ring-4 ring-white/90"
        >
          <Icon name="plus" className="size-8" />
        </button>
      </div>

      {!available && (
        <span className="display-upright absolute top-5 right-5 rounded-[var(--radius-pill)] bg-[var(--color-dark)] px-5 py-2 text-[length:var(--text-kiosk-sm)] text-white uppercase">
          {product.onlyTakeIn && orderType === 'take_out' ? t('menu.eatInOnly') : t('menu.soldOut')}
        </span>
      )}

      {/* Scarcity, only where it is true. Infinite stock is most of the menu. */}
      {available && !product.isInfinite && product.balance <= LOW_STOCK && (
        <span className="display-upright absolute top-5 right-5 rounded-[var(--radius-pill)] bg-[var(--color-warn)] px-5 py-2 text-[length:var(--text-kiosk-sm)] text-[var(--color-brand-ink)] uppercase tabular-nums">
          {product.balance} {t('menu.lowStock')}
        </span>
      )}
    </div>
  )
})
